/*
 * Purpose: Flow Templates catalog SSOT — routes, featured picks, metadata
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { APP_ROUTES } from '@/lib/appRoutes';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import type { Workflow } from '@/stores/workflowStore';
import { WORKFLOW_TEMPLATES } from '@/services/templates';
import { ensureMissionAnchors, inferWorkflowOutcome } from '@/lib/missionAnchors';

export type FlowTemplateRecord = Workflow & {
  difficulty: string;
  category: string;
  features: string[];
  useCase?: string;
  templateKey: string;
};

/** Canonical path — prefer over `?tab=templates`. */
export const FLOW_TEMPLATES_PATH = APP_ROUTES.flowTemplates;

/** Shown on dashboard spotlight and My flows inline strip. */
export const FEATURED_TEMPLATE_KEYS = [
  'linkedin_job_hunt_pipeline',
  'simple_email_alert',
  'receipt_email_to_tracker',
] as const;

export type FeaturedTemplateKey = (typeof FEATURED_TEMPLATE_KEYS)[number];

function toRecord(key: string, template: FlowTemplateRecord): FlowTemplateRecord {
  return { ...template, templateKey: key };
}

/** Full catalog with stable keys for deep links and analytics. */
export function listFlowTemplates(): FlowTemplateRecord[] {
  return Object.entries(WORKFLOW_TEMPLATES).map(([key, template]) =>
    toRecord(key, template as FlowTemplateRecord)
  );
}

export function getFlowTemplateByKey(key: string): FlowTemplateRecord | undefined {
  const template = WORKFLOW_TEMPLATES[key];
  if (!template) return undefined;
  return toRecord(key, template as FlowTemplateRecord);
}

export function getFeaturedFlowTemplates(): FlowTemplateRecord[] {
  return FEATURED_TEMPLATE_KEYS.map((key) => getFlowTemplateByKey(key)).filter(
    (t): t is FlowTemplateRecord => Boolean(t)
  );
}

export function flowTemplateCount(): number {
  return listFlowTemplates().length;
}

export interface LearningFlowStepMeta {
  templateKey: string;
  order: number;
  estMinutes: number;
  lesson: string;
  ctaLabel: string;
}

/** Beginner → expert curriculum — SSOT for dashboard + templates Learning tab. */
export const LEARNING_FLOW_STEPS: LearningFlowStepMeta[] = [
  {
    templateKey: 'simple_email_alert',
    order: 1,
    estMinutes: 10,
    lesson: 'Start here — triggers, transforms, and notification actions.',
    ctaLabel: 'Start learning',
  },
  {
    templateKey: 'intermediate_data_validation',
    order: 2,
    estMinutes: 20,
    lesson: 'Conditional routing, validation, and error branches.',
    ctaLabel: 'Next level',
  },
  {
    templateKey: 'advanced_api_orchestration',
    order: 3,
    estMinutes: 30,
    lesson: 'Multi-step API calls with transforms and orchestration.',
    ctaLabel: 'Next level',
  },
  {
    templateKey: 'expert_data_pipeline',
    order: 4,
    estMinutes: 45,
    lesson: 'Production pipelines — enrichment, storage, and analytics.',
    ctaLabel: 'Expert track',
  },
];

export type LearningFlowStep = LearningFlowStepMeta & { template: FlowTemplateRecord };

export function getLearningFlows(): LearningFlowStep[] {
  return LEARNING_FLOW_STEPS.map((step) => {
    const template = getFlowTemplateByKey(step.templateKey);
    if (!template) return null;
    return { ...step, template };
  }).filter((s): s is LearningFlowStep => Boolean(s));
}

export function flowTemplateDisplayName(name: string): string {
  return name.replace(/^[^\w]+\s*/, '').trim() || name;
}

/** Clone payload for importWorkflow — strips catalog metadata and ensures Mission anchors. */
export function buildClonedFlowTemplate(template: FlowTemplateRecord) {
  const { difficulty, category, features, useCase, templateKey, ...workflow } = template;
  void difficulty;
  void category;
  void features;
  void useCase;
  void templateKey;

  const base: Workflow = {
    ...workflow,
    name: `${flowTemplateDisplayName(template.name)} (from template)`,
    nodes: template.nodes.map((node) => ({ ...node, config: { ...node.config } })),
    edges: template.edges.map((edge) => ({ ...edge })),
    createdAt: new Date(),
    updatedAt: new Date(),
    missionMeta: workflow.missionMeta ?? {
      allowDynamicMap: true,
      mapLocked: false,
      flowOrientation: 'auto',
      flowTracingMode: false,
      smartAutoConfigureOnConnect: true,
      smartAutoFixPolicy: 'safe-and-review',
      smartInputModalBehavior: 'badge-only',
      truthFactorStrictness: 'balanced',
    },
  };

  const outcome =
    String(template.description ?? '').trim() ||
    inferWorkflowOutcome({ ...base, name: flowTemplateDisplayName(template.name) });

  return ensureMissionAnchors(base, { outcome, minimalRunnable: false });
}

export function flowTemplatesLearningHref(): string {
  return `${APP_ROUTES.flowTemplates}?view=learning`;
}

export { getAllTemplates, getTemplateById, WORKFLOW_TEMPLATES } from '@/services/templates';

export const FLOW_TEMPLATES_IA = {
  label: PRODUCT_TERMS.flowTemplates,
  shortLabel: 'Templates',
  description: 'Clone production-ready starter flows — customize on the Mission map.',
  path: FLOW_TEMPLATES_PATH,
  parentPath: APP_ROUTES.flows,
  parentLabel: 'Flows',
} as const;
